import { useEffect, useState } from "react";
import { Search, User, ShoppingBag, Menu, X } from "lucide-react";

export default function Navbar({ cartCount = 2 }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = ["Shop", "Men", "Women", "Collections", "About"];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#f5f3ee]/90 backdrop-blur-md border-b border-black/5 shadow-[0_10px_30px_-20px_rgba(0,0,0,0.2)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
        <a
          href="#top"
          data-testid="navbar-logo"
          className="flex items-center gap-2 group"
          aria-label="STRYDE home"
        >
          <div className="w-8 h-8 rounded-lg bg-[#0e0e10] flex items-center justify-center transition-transform group-hover:rotate-12">
            <span className="text-[#ff5722] font-display font-bold text-lg leading-none">S</span>
          </div>
          <span className="font-display font-extrabold text-xl tracking-tight">STRYDE</span>
        </a>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {links.map((l) => (
            <a
              key={l}
              href={`#${l.toLowerCase()}`}
              data-testid={`nav-link-${l.toLowerCase()}`}
              className="link-sweep text-sm font-medium hover:text-[#ff5722] transition-colors"
            >
              {l}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-1 md:gap-2">
          <button
            data-testid="navbar-search"
            className="p-2.5 rounded-full hover:bg-black/5 transition-colors"
            aria-label="Search"
          >
            <Search size={18} strokeWidth={2} />
          </button>
          <button
            data-testid="navbar-account"
            className="p-2.5 rounded-full hover:bg-black/5 transition-colors"
            aria-label="Account"
          >
            <User size={18} strokeWidth={2} />
          </button>
          <button
            data-testid="navbar-cart"
            className="relative p-2.5 rounded-full hover:bg-black/5 transition-colors"
            aria-label={`Cart with ${cartCount} items`}
          >
            <ShoppingBag size={18} strokeWidth={2} />
            {cartCount > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#ff5722] text-white text-[10px] font-bold flex items-center justify-center"
              >
                {cartCount}
              </span>
            )}
          </button>
          <button
            data-testid="navbar-mobile-toggle"
            className="md:hidden p-2.5 rounded-full hover:bg-black/5 transition-colors"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="md:hidden bg-[#f5f3ee] border-t border-black/5 px-5 py-4 flex flex-col gap-3"
        >
          {links.map((l) => (
            <a
              key={l}
              href={`#${l.toLowerCase()}`}
              onClick={() => setOpen(false)}
              className="py-2 text-base font-medium"
            >
              {l}
            </a>
          ))}
        </div>
      )}
    </header>
  );
}
